import React from 'react';
import { isNil, keyBy } from 'lodash';

import type { AdjusterUser } from '~/Adjuster/useFetchAdjusters';
import useFetchAdjusters from '~/Adjuster/useFetchAdjusters';
import { AI_NAME } from '~/components/AiChat/components/Message/MessageTypes';
import { useClaim } from '~/components/ClaimContainer';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import EmptyState from '~/components/core/EmptyState';
import PermissionsRenderWrapper from '~/components/core/Permissions/PermissionsRenderWrapper';
import { PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import SortableTable from '~/components/core/Tables/SortableTable';
import PencilIcon from '~/components/icons/PencilIcon';
import { serverDateFormatWithoutTZChange } from '~/DateTimeUtils';
import type { ClaimMilestonesResponseClaimMilestoneModel as ClaimMilestone } from '~/server_shared/openapi/data-contracts';
import { ClaimMilestonesResponseClaimChangeSource } from '~/server_shared/openapi/data-contracts';

import TEXTS from '../texts';

// A day the Conductor recorded shows as Clive's, as a note Clive wrote does.
const getRecordedBy = ({ source, set_by_user_id }: ClaimMilestone, usersById: Record<string, AdjusterUser>): string => {
  if (source === ClaimMilestonesResponseClaimChangeSource.AGENT) {
    return AI_NAME;
  }

  return isNil(set_by_user_id) ? '' : (usersById[set_by_user_id]?.username ?? '');
};

interface ClaimMilestonesTableProps {
  milestones: ClaimMilestone[];
  onEdit: (milestone: ClaimMilestone) => void;
}

const ClaimMilestonesTable: React.FC<ClaimMilestonesTableProps> = ({ milestones, onEdit }) => {
  const { claim } = useClaim({ claimRequired: true });
  // Removed users are kept so a date they recorded still shows their name.
  const { possibleAdjustersWithRemoved } = useFetchAdjusters(claim.organization_id);
  const usersById = keyBy(possibleAdjustersWithRemoved, 'id');

  const columns = [
    { id: 'label', label: TEXTS.columns.milestone },
    {
      id: 'occurred_on',
      label: TEXTS.columns.reachedOn,
      specialCell: ({ occurred_on }: ClaimMilestone) =>
        isNil(occurred_on) ? TEXTS.notReached : serverDateFormatWithoutTZChange(occurred_on),
    },
    {
      id: 'recorded_by',
      label: TEXTS.columns.recordedBy,
      specialCell: (milestone: ClaimMilestone) => getRecordedBy(milestone, usersById),
    },
    {
      id: 'actions',
      label: '',
      // The record and clear routes check the profile's default verb, not a claim permission.
      specialCell: (milestone: ClaimMilestone) => (
        <PermissionsRenderWrapper verb={PERMISSION_VERBS.WRITE}>
          <IconButton onClick={() => onEdit(milestone)} aria-label={`${TEXTS.edit} ${milestone.label}`}>
            <PencilIcon />
          </IconButton>
        </PermissionsRenderWrapper>
      ),
    },
  ];

  return (
    <SortableTable
      rows={milestones.map((milestone) => ({ ...milestone, id: milestone.key }))}
      columns={columns}
      // The server sends the milestones in their configured order.
      keepRowsOrder
      disableSortByUser
      emptyStateComponent={<EmptyState subtitle={TEXTS.emptyState} illustration={<></>} />}
    />
  );
};

export default ClaimMilestonesTable;
